import type { EnvironmentConfig } from '../../config/env.ts';
import { createBusiness } from '../business.ts';
import { createDiscover, createReports, createSaved } from '../discover.ts';
import { createHandles } from '../handles.ts';
import { createListings } from '../listings.ts';
import { createMedia } from '../media.ts';
import { createOrders, createRider } from '../orders.ts';
import { createPayouts } from '../payouts.ts';
import { createPolicies } from '../policies.ts';
import { createAdmin, createProblems } from '../admin.ts';
import { createGuilds } from '../guilds.ts';
import { createRiders } from '../riders.ts';
import { createSocial } from '../social.ts';
import type { DataLayer } from '../ports.ts';
import { createProfiles } from '../profiles.ts';
import { createReviews } from '../reviews.ts';
import { createEmailCodes } from '../email-codes.ts';
import { createShops } from '../shops.ts';
import { createWorkerClient } from '../worker.ts';
import { createSupport } from '../support.ts';
import { createReferrals } from '../referrals.ts';
import { createActivity } from '../activity.ts';
import { createBlocks } from '../blocks.ts';
import { initializeFirebase } from './app.ts';
import { createAccountSettings } from './account.ts';
import { createFirebaseAuth } from './auth.ts';
import { countDocuments, queryDocuments } from './query.ts';
import { readDocument, readShopRecords } from './shops.ts';
import { markTyping, readShowActive, trackPresence, watchActive, watchTyping } from './presence.ts';
import { watchDocument, watchQuery } from './watch.ts';

export function createFirebaseDataLayer(config: EnvironmentConfig): DataLayer {
  const services = initializeFirebase(config);
  const auth = createFirebaseAuth(services);
  const worker = createWorkerClient({ baseUrl: config.workerUrl, idToken: auth.idToken });
  const read = (collection: string, id: string) => readDocument(services, collection, id);
  const readHandleRecord = (handle: string) => read('handles', handle);
  const blocks = createBlocks({ worker, currentUid: () => services.auth.currentUser?.uid ?? null });
  const hidden = () => blocks.filter();
  return {
    blocks,
    auth,
    support: createSupport(worker),
    referrals: createReferrals({ worker, baseUrl: config.workerUrl }),
    activity: createActivity(worker),
    policies: createPolicies(worker),
    handles: createHandles({ readHandleRecord }),
    profiles: createProfiles({ worker, readHandleRecord, readProfile: uid => read('profiles', uid) }),
    shops: createShops({ worker, readHandleRecord, readShopRecords: shopId => readShopRecords(services, shopId) }),
    listings: createListings({ worker, hidden, readDocument: read, query: (collection, options) => queryDocuments(services, collection, options) }),
    business: createBusiness(worker),
    media: createMedia(worker),
    discover: createDiscover((collection, options) => queryDocuments(services, collection, options), hidden),
    saved: createSaved({ worker, hidden, readDocument: read, query: collection => queryDocuments(services, collection, { limit: 200 }), currentUid: () => services.auth.currentUser?.uid ?? null }),
    reports: createReports(worker),
    orders: createOrders({
      worker, baseUrl: config.workerUrl, readDocument: read, currentUid: () => services.auth.currentUser?.uid ?? null,
      watchQuery: (collection, options, onData, onError) => watchQuery(services, collection, options, onData, onError),
      watchDocument: (path, onData, onError) => watchDocument(services, path, onData, onError),
    }),
    rider: createRider({ baseUrl: config.workerUrl }),
    problems: createProblems(worker),
    admin: createAdmin(worker),
    account: createAccountSettings(services, worker),
    riders: createRiders(worker),
    emailCodes: createEmailCodes({ worker, baseUrl: config.workerUrl }),
    reviews: createReviews({ worker, query: (collection, options) => queryDocuments(services, collection, options) }),
    payouts: createPayouts({ worker, query: (collection, options) => queryDocuments(services, collection, options), currentUid: () => services.auth.currentUser?.uid ?? null }),
    social: createSocial({
      worker, hidden, readDocument: read, currentUid: () => services.auth.currentUser?.uid ?? null,
      query: (collection, options) => queryDocuments(services, collection, options),
      count: (collection, filters) => countDocuments(services, collection, filters),
      watchQuery: (collection, options, onData, onError) => watchQuery(services, collection, options, onData, onError),
      watchDocument: (path, onData, onError) => watchDocument(services, path, onData, onError),
      trackPresence: (uid, showActive) => trackPresence(services, uid, showActive),
      watchActive: (uid, listener) => watchActive(services, uid, listener),
      readShowActive: uid => readShowActive(services, uid),
      watchTyping: (threadId, listener) => watchTyping(services, threadId, listener, 'dm_typing'),
      markTyping: (threadId, uid) => markTyping(services, threadId, uid, 'dm_typing'),
    }),
    guilds: createGuilds({
      worker, hidden, readDocument: read, currentUid: () => services.auth.currentUser?.uid ?? null,
      query: (collection, options) => queryDocuments(services, collection, options),
      count: (collection, filters) => countDocuments(services, collection, filters),
      watchQuery: (collection, options, onData, onError) => watchQuery(services, collection, options, onData, onError),
      watchTyping: (guildId, listener) => watchTyping(services, guildId, listener),
      markTyping: (guildId, uid) => markTyping(services, guildId, uid),
    }),
  };
}
